import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { 
  Users, 
  Plus, 
  Edit,
  Trash2,
  Search,
  Filter,
  Settings,
  Shield,
  UserPlus,
  UserMinus,
  Mail,
  Phone,
  MapPin,
  Clock,
  Activity,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Crown,
  Award,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Calendar,
  Globe,
  Smartphone,
  Key,
  Bell,
  MoreHorizontal,
  Download,
  Upload
} from 'lucide-react';
import { TeamMember, Role, Permission, Team, UserInvitation, UserActivity } from '../../types/UserTypes';

// Mock data for team members
const mockTeamMembers: Record<string, TeamMember[]> = {
  'tenant1': [
    {
      id: 'member1',
      userId: 'user1',
      email: 'john.admin@university.edu',
      name: 'John Anderson',
      avatar: undefined,
      status: 'active',
      roles: [
        {
          roleId: 'role1',
          roleName: 'Organization Administrator',
          assignedAt: '2024-01-15',
          assignedBy: 'system',
          isTemporary: false
        }
      ],
      directPermissions: [],
      profile: {
        title: 'IT Administrator',
        department: 'Information Technology',
        location: 'Campus Main',
        timezone: 'America/New_York',
        preferredLanguage: 'en',
        phoneNumber: '+1-555-0123',
        lastLoginAt: '2024-03-22T14:30:00Z',
        loginCount: 247
      },
      security: {
        mfaEnabled: true,
        mfaMethod: 'app',
        lastPasswordChange: '2024-02-15',
        failedLoginAttempts: 0,
        trustedDevices: [
          {
            id: 'device1',
            name: 'MacBook Pro',
            fingerprint: 'abc123...',
            lastUsed: '2024-03-22T14:30:00Z',
            trusted: true
          }
        ]
      },
      activity: {
        lastActivity: '2024-03-22T14:30:00Z',
        actionsToday: 23,
        actionsThisMonth: 456,
        credentialsManaged: 89,
        policiesCreated: 12,
        agentsCreated: 3
      },
      invitedAt: '2024-01-15',
      invitedBy: 'system',
      joinedAt: '2024-01-15',
      updatedAt: '2024-03-20'
    },
    {
      id: 'member2',
      userId: 'user2',
      email: 'sarah.registrar@university.edu',
      name: 'Sarah Johnson',
      status: 'active',
      roles: [
        {
          roleId: 'role2',
          roleName: 'Registrar',
          assignedAt: '2024-01-20',
          assignedBy: 'user1',
          isTemporary: false
        }
      ],
      directPermissions: [],
      profile: {
        title: 'Senior Registrar',
        department: 'Academic Affairs',
        location: 'Administration Building',
        timezone: 'America/New_York',
        preferredLanguage: 'en',
        phoneNumber: '+1-555-0124',
        lastLoginAt: '2024-03-22T10:15:00Z',
        loginCount: 189
      },
      security: {
        mfaEnabled: true,
        mfaMethod: 'sms',
        lastPasswordChange: '2024-01-20',
        failedLoginAttempts: 0,
        trustedDevices: []
      },
      activity: {
        lastActivity: '2024-03-22T10:15:00Z',
        actionsToday: 15,
        actionsThisMonth: 234,
        credentialsManaged: 156,
        policiesCreated: 5,
        agentsCreated: 1
      },
      invitedAt: '2024-01-20',
      invitedBy: 'user1',
      joinedAt: '2024-01-20',
      updatedAt: '2024-03-18'
    },
    {
      id: 'member3',
      userId: 'user3',
      email: 'mike.support@university.edu',
      name: 'Mike Chen',
      status: 'pending',
      roles: [
        {
          roleId: 'role3',
          roleName: 'Support Specialist',
          assignedAt: '2024-03-20',
          assignedBy: 'user1',
          isTemporary: false
        }
      ],
      directPermissions: [],
      profile: {
        title: 'Technical Support',
        department: 'IT Support',
        timezone: 'America/New_York',
        preferredLanguage: 'en',
        loginCount: 0
      },
      security: {
        mfaEnabled: false,
        failedLoginAttempts: 0,
        trustedDevices: []
      },
      activity: {
        lastActivity: '',
        actionsToday: 0,
        actionsThisMonth: 0,
        credentialsManaged: 0,
        policiesCreated: 0,
        agentsCreated: 0
      },
      invitedAt: '2024-03-20',
      invitedBy: 'user1',
      updatedAt: '2024-03-20'
    }
  ]
};

// Mock roles
const mockRoles: Role[] = [
  {
    id: 'role1',
    name: 'Organization Administrator',
    description: 'Full administrative access to all organizational resources',
    type: 'system',
    level: 'org_admin',
    permissions: [],
    isActive: true,
    createdAt: '2024-01-01',
    updatedAt: '2024-01-01',
    createdBy: 'system',
    constraints: {
      requireApproval: false
    },
    features: {
      canDelegatePermissions: true,
      canCreateSubRoles: true,
      canManageTeam: true,
      canAccessBilling: true,
      canConfigureIntegrations: true,
      canViewAuditLogs: true
    }
  },
  {
    id: 'role2',
    name: 'Registrar',
    description: 'Manages student records and credential issuance',
    type: 'system',
    level: 'team_lead',
    permissions: [],
    isActive: true,
    createdAt: '2024-01-01',
    updatedAt: '2024-01-01',
    createdBy: 'system',
    constraints: {
      requireApproval: false
    },
    features: {
      canDelegatePermissions: false,
      canCreateSubRoles: false,
      canManageTeam: true,
      canAccessBilling: false,
      canConfigureIntegrations: false,
      canViewAuditLogs: true
    }
  },
  {
    id: 'role3',
    name: 'Support Specialist',
    description: 'Provides technical support and user assistance',
    type: 'system',
    level: 'member',
    permissions: [],
    isActive: true,
    createdAt: '2024-01-01',
    updatedAt: '2024-01-01',
    createdBy: 'system',
    constraints: {
      requireApproval: false
    },
    features: {
      canDelegatePermissions: false,
      canCreateSubRoles: false,
      canManageTeam: false,
      canAccessBilling: false,
      canConfigureIntegrations: false,
      canViewAuditLogs: false
    }
  }
];

// Mock permissions
const mockPermissions: Permission[] = [
  {
    id: 'perm1',
    name: 'Manage Credentials',
    description: 'Create, update, and delete credentials',
    category: 'credentials',
    actions: ['create', 'read', 'update', 'delete'],
    scope: 'organization'
  },
  {
    id: 'perm2',
    name: 'Manage Policies',
    description: 'Create and configure verification policies',
    category: 'policies',
    actions: ['create', 'read', 'update', 'delete'],
    scope: 'organization'
  },
  {
    id: 'perm3',
    name: 'Manage Users',
    description: 'Invite and manage team members',
    category: 'users',
    actions: ['create', 'read', 'update', 'delete'],
    scope: 'organization'
  },
  {
    id: 'perm4',
    name: 'View Analytics',
    description: 'Access analytics and reports',
    category: 'analytics',
    actions: ['read'],
    scope: 'organization'
  }
];

export default function UserManagement() {
  const { tenant, user } = useAuth();
  const [activeTab, setActiveTab] = useState('members');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showMemberDialog, setShowMemberDialog] = useState(false);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  // Get tenant-specific members
  const tenantId = tenant?.id || 'tenant1';
  const members = mockTeamMembers[tenantId] || [];

  const filteredMembers = members.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.profile.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-700 bg-green-100';
      case 'inactive': return 'text-gray-700 bg-gray-100';
      case 'pending': return 'text-yellow-700 bg-yellow-100';
      case 'suspended': return 'text-red-700 bg-red-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const getRoleIcon = (level: string) => {
    switch (level) {
      case 'org_admin': return <Crown className="h-4 w-4" />;
      case 'team_lead': return <Award className="h-4 w-4" />;
      case 'member': return <Users className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-charcoal-ink">User Management</h2>
          <p className="text-warm-slate">Manage team members, roles, and permissions</p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => setShowInviteDialog(true)}
            className="credential-capital-button-primary"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Invite User
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white credential-capital-shadow">
          <TabsTrigger value="members" className="data-[state=active]:bg-electric-steel data-[state=active]:text-white">
            <Users className="h-4 w-4 mr-2" />
            Team Members
          </TabsTrigger>
          <TabsTrigger value="roles" className="data-[state=active]:bg-electric-steel data-[state=active]:text-white">
            <Shield className="h-4 w-4 mr-2" />
            Roles & Permissions
          </TabsTrigger>
          <TabsTrigger value="invitations" className="data-[state=active]:bg-electric-steel data-[state=active]:text-white">
            <Mail className="h-4 w-4 mr-2" />
            Invitations
          </TabsTrigger>
          <TabsTrigger value="activity" className="data-[state=active]:bg-electric-steel data-[state=active]:text-white">
            <Activity className="h-4 w-4 mr-2" />
            Activity
          </TabsTrigger>
        </TabsList>

        {/* Team Members Tab */}
        <TabsContent value="members" className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-warm-slate" />
              <Input
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 credential-capital-input"
              />
            </div>
            <div className="flex space-x-2">
              <Button variant="outline">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </Button>
              <Button variant="outline">
                <Settings className="h-4 w-4 mr-2" />
                Bulk Actions
              </Button>
            </div>
          </div>

          {/* Member Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Total Members</p>
                    <p className="text-3xl font-bold text-charcoal-ink">{members.length}</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-xl">
                    <Users className="h-8 w-8 text-electric-steel" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Active Members</p>
                    <p className="text-3xl font-bold text-charcoal-ink">
                      {members.filter(m => m.status === 'active').length}
                    </p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-xl">
                    <CheckCircle className="h-8 w-8 text-verified-green" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">Pending Invites</p>
                    <p className="text-3xl font-bold text-charcoal-ink">
                      {members.filter(m => m.status === 'pending').length}
                    </p>
                  </div>
                  <div className="bg-yellow-50 p-3 rounded-xl">
                    <Clock className="h-8 w-8 text-yellow-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="credential-capital-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warm-slate">MFA Enabled</p>
                    <p className="text-3xl font-bold text-charcoal-ink">
                      {members.filter(m => m.security.mfaEnabled).length}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-xl">
                    <Shield className="h-8 w-8 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Members Table */}
          <Card className="credential-capital-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <div className="bg-blue-100 p-2 rounded-full">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-charcoal-ink">{member.name}</p>
                          <p className="text-sm text-warm-slate">{member.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {getRoleIcon(member.roles[0]?.roleName.includes('Administrator') ? 'org_admin' : 
                                    member.roles[0]?.roleName.includes('Registrar') ? 'team_lead' : 'member')}
                        <span className="text-sm">{member.roles[0]?.roleName}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{member.profile.department || 'Not set'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col space-y-1">
                        <Badge className={getStatusColor(member.status)} variant="outline">
                          {member.status}
                        </Badge>
                        {member.security.mfaEnabled && (
                          <div className="flex items-center space-x-1">
                            <Shield className="h-3 w-3 text-green-600" />
                            <span className="text-xs text-green-600">MFA</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {member.profile.lastLoginAt ? (
                          <>
                            <p>{new Date(member.profile.lastLoginAt).toLocaleDateString()}</p>
                            <p className="text-xs text-warm-slate">
                              {member.activity.actionsToday} actions today
                            </p>
                          </>
                        ) : (
                          <span className="text-warm-slate">Never</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedMember(member);
                            setShowMemberDialog(true);
                          }}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <MoreHorizontal className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Roles & Permissions Tab */}
        <TabsContent value="roles" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-charcoal-ink">Roles & Permissions</h3>
              <p className="text-warm-slate">Define roles and assign fine-grained permissions</p>
            </div>
            <Button
              onClick={() => setShowRoleDialog(true)}
              className="credential-capital-button-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Role
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Roles */}
            <Card className="credential-capital-card">
              <CardHeader>
                <CardTitle>Roles</CardTitle>
                <CardDescription>Predefined sets of permissions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {mockRoles.map((role) => (
                    <div key={role.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          {getRoleIcon(role.level)}
                          <div>
                            <h4 className="font-medium text-charcoal-ink">{role.name}</h4>
                            <p className="text-sm text-warm-slate">{role.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant={role.type === 'system' ? 'default' : 'secondary'}>
                            {role.type}
                          </Badge>
                          <Switch checked={role.isActive} />
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex space-x-4">
                          <span className="text-warm-slate">
                            Users: {members.filter(m => m.roles.some(r => r.roleId === role.id)).length}
                          </span>
                          <span className="text-warm-slate">Level: {role.level}</span>
                        </div>
                        <div className="flex space-x-1">
                          <Button variant="outline" size="sm">
                            <Edit className="h-3 w-3" />
                          </Button>
                          {role.type === 'custom' && (
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Permissions */}
            <Card className="credential-capital-card">
              <CardHeader>
                <CardTitle>Permissions</CardTitle>
                <CardDescription>Available permissions by category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(
                    mockPermissions.reduce((acc, perm) => {
                      if (!acc[perm.category]) acc[perm.category] = [];
                      acc[perm.category].push(perm);
                      return acc;
                    }, {} as Record<string, Permission[]>)
                  ).map(([category, permissions]) => (
                    <div key={category} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-charcoal-ink mb-3 capitalize">{category}</h4>
                      <div className="space-y-2">
                        {permissions.map((permission) => (
                          <div key={permission.id} className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-charcoal-ink">{permission.name}</p>
                              <p className="text-xs text-warm-slate">{permission.description}</p>
                            </div>
                            <div className="flex space-x-1">
                              {permission.actions.map((action) => (
                                <Badge key={action} variant="outline" className="text-xs">
                                  {action}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Invitations Tab */}
        <TabsContent value="invitations" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-charcoal-ink">Pending Invitations</h3>
              <p className="text-warm-slate">Manage user invitations and onboarding</p>
            </div>
          </div>

          <Card className="credential-capital-card">
            <CardContent className="pt-6 text-center py-12">
              <Mail className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-charcoal-ink mb-2">No pending invitations</h3>
              <p className="text-warm-slate mb-6">All team members have accepted their invitations.</p>
              <Button
                onClick={() => setShowInviteDialog(true)}
                className="credential-capital-button-primary"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Invite New Member
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-charcoal-ink">User Activity</h3>
              <p className="text-warm-slate">Monitor team member activities and actions</p>
            </div>
          </div>

          <Card className="credential-capital-card">
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest actions performed by team members</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {members.filter(m => m.activity.lastActivity).map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-100 p-2 rounded-full">
                        <Activity className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-charcoal-ink">{member.name}</p>
                        <p className="text-sm text-warm-slate">
                          {member.activity.actionsToday} actions today • {member.activity.actionsThisMonth} this month
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-charcoal-ink">
                        Last active: {member.activity.lastActivity ? new Date(member.activity.lastActivity).toLocaleString() : 'Never'}
                      </p>
                      <div className="flex space-x-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {member.activity.credentialsManaged} credentials
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {member.activity.policiesCreated} policies
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Member Detail Dialog */}
      <Dialog open={showMemberDialog} onOpenChange={setShowMemberDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Member Details</DialogTitle>
            <DialogDescription>View and manage team member information</DialogDescription>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Basic Information</Label>
                    <div className="mt-2 space-y-2">
                      <p><strong>Name:</strong> {selectedMember.name}</p>
                      <p><strong>Email:</strong> {selectedMember.email}</p>
                      <p><strong>Status:</strong> 
                        <Badge className={getStatusColor(selectedMember.status)} variant="outline">
                          {selectedMember.status}
                        </Badge>
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Profile</Label>
                    <div className="mt-2 space-y-2 text-sm">
                      <p><strong>Title:</strong> {selectedMember.profile.title || 'Not set'}</p>
                      <p><strong>Department:</strong> {selectedMember.profile.department || 'Not set'}</p>
                      <p><strong>Location:</strong> {selectedMember.profile.location || 'Not set'}</p>
                      <p><strong>Timezone:</strong> {selectedMember.profile.timezone}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Security</Label>
                    <div className="mt-2 space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <Shield className={`h-4 w-4 ${selectedMember.security.mfaEnabled ? 'text-green-600' : 'text-gray-400'}`} />
                        <span>MFA: {selectedMember.security.mfaEnabled ? 'Enabled' : 'Disabled'}</span>
                      </div>
                      <p><strong>Trusted Devices:</strong> {selectedMember.security.trustedDevices.length}</p>
                      <p><strong>Failed Attempts:</strong> {selectedMember.security.failedLoginAttempts}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Activity Summary</Label>
                    <div className="mt-2 space-y-2 text-sm">
                      <p><strong>Login Count:</strong> {selectedMember.profile.loginCount}</p>
                      <p><strong>Actions Today:</strong> {selectedMember.activity.actionsToday}</p>
                      <p><strong>Actions This Month:</strong> {selectedMember.activity.actionsThisMonth}</p>
                      <p><strong>Credentials Managed:</strong> {selectedMember.activity.credentialsManaged}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Roles</Label>
                <div className="mt-2 space-y-2">
                  {selectedMember.roles.map((role) => (
                    <div key={role.roleId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm font-medium">{role.roleName}</span>
                      <div className="text-xs text-warm-slate">
                        Assigned: {new Date(role.assignedAt).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>Send an invitation to join your organization</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <Input id="invite-email" type="email" placeholder="user@example.com" className="credential-capital-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select>
                <SelectTrigger className="credential-capital-input">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {mockRoles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-message">Personal Message (Optional)</Label>
              <Textarea 
                id="invite-message" 
                placeholder="Welcome to our team..." 
                className="credential-capital-input"
              />
            </div>
            <div className="flex space-x-2">
              <Button className="credential-capital-button-primary flex-1">
                <Mail className="h-4 w-4 mr-2" />
                Send Invitation
              </Button>
              <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}